<script setup>
import { onMounted, provide, ref, watch, inject, nextTick } from 'vue'
import { PencilSquareIcon } from '@heroicons/vue/24/solid'
import { Plus } from 'lucide-vue-next'
import { PrimaryButton } from '@codinglabsau/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@codinglabsau/gooey'
import Modal from '@/Components/Inputs/BlockEditor/Extension/Components/Modal.vue'
import ToolbarButton from '@/Components/Inputs/BlockEditor/Extension/Components/ToolbarButton.vue'
import {
  DesktopMenuList,
  ResponsiveMenuList,
  componentFormMap,
  useNavigationItems,
  useViewport,
} from '@/Components/Navigation'
import ResizablePreview from '@/Components/ResizablePreview.vue'

const props = defineProps({
  editor: {
    default: null,
  },
})

const {
  items,
  elementOptions,
  selectedItem,
  removeItem,
  selectItem,
  deselectItem,
} = useNavigationItems()

const value = defineModel('items')
const previewContent = ref('')

onMounted(() => props.editor.callbacks.update.push((e) => {
  previewContent.value = e.editor.getHTML()
}))

const showDialogue = ref(false)
watch(showDialogue, (value) => {
  if (value === true) {
    previewContent.value = props.editor.getHTML()
  }
})

const { viewport, viewportOptions } = useViewport()
provide('viewport', viewport)

onMounted(() => items.value = value.value)
watch(items, () => {
  value.value = items.value
}, { deep: true })

provide('items', items)
provide('removeItem', removeItem)
provide('selectedItem', selectedItem)
provide('selectItem', selectItem)
provide('deselectItem', deselectItem)

const getParentForNewItem = (componentType) => {
  // Selected item will be the parent if it allows children
  if (selectedItem.value &&
      selectedItem.value.hasChildren &&
      selectedItem.value.allowedChildren.includes(componentType)) {
    return selectedItem.value
  }

  // Selected item has a parent, so use that as a parent
  if (selectedItem.value && selectedItem.value.parent_id) {
    const parentItem = items.value[selectedItem.value.parent_id]
    if (parentItem &&
        parentItem.hasChildren &&
        parentItem.allowedChildren.includes(componentType)) {
      return parentItem
    }
  }

  // has no parent (added as a root level menu item)
  return null
}
</script>

<template>
  <ToolbarButton
    :icon="PencilSquareIcon"
    :label="__('Edit Navigation')"
    v-model="showDialogue"
  />
  <Modal size="max-w-7xl" v-model="showDialogue">
    <template #title>
      {{ __('Edit Navigation') }}
    </template>

    <div class="flex items-center justify-center">
      <Tabs v-model="viewport" default-value="desktop">
        <TabsList class="bg-gray-100">
          <TabsTrigger
            v-for="v in viewportOptions"
            :key="v.value"
            :value="v.value"
            :class="{ 'bg-indigo-500 text-white': viewport === v.value }"
          >
            {{ v.label }}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    <ResizablePreview
      :content="previewContent"
      :preview-url="route('hq.widgets.preview')"
      :viewport="viewport"
    />

    <div>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <div class="flex items-center gap-2">
            <Plus class="size-4" />
            <span>{{ __('Add Menu Item') }}</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="bg-white">
          <DropdownMenuItem
            v-for="option in elementOptions"
            @click.prevent="option.action(getParentForNewItem(option.component))"
          >
            <Component :is="option.icon" class="size-3 mr-2" />
            {{ option.name }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div class="flex mt-4">
        <div class="w-1/2 border-l border-y">
          <DesktopMenuList v-if="viewport === 'desktop'" v-model="items" />
          <ResponsiveMenuList v-else-if="viewport === 'tablet'" v-model="items" />
          <ResponsiveMenuList v-else-if="viewport === 'mobile'" v-model="items" />
        </div>
        <div class="w-1/2 border-y bg-white">
          <div v-if="selectedItem" class="flex flex-col p-4 gap-2">
            <component
              :key="selectedItem.id"
              :is="componentFormMap[selectedItem.component]"
              :id="selectedItem.id"
              v-model:name="items[selectedItem.id].name"
              v-model:href="items[selectedItem.id].href"
              v-model:switchesTenant="items[selectedItem.id].switchesTenant"
              v-model:tenant="items[selectedItem.id].tenant"
              v-model:icon="items[selectedItem.id].icon"
              v-model:viewport="items[selectedItem.id].viewport"
              v-model:meta="items[selectedItem.id].meta"
            />
          </div>
        </div>
      </div>

      <PrimaryButton
        @click.prevent="showDialogue = false"
        class="mt-4" as="button"
      >
        {{ __('Done') }}
      </PrimaryButton>
    </div>

  </Modal>
</template>
